import { OPERATION } from '@/constants';
import CookieService from '@/libs/cookie-service';
import { LOCALE_MAP } from '@/shared/constants';

/**
 * Creates a promise that resolves after a specified delay.
 * @param delay The delay in milliseconds before the promise resolves. Default is 700ms.
 * @returns A promise that resolves after the specified delay.
 */
export const wait = (delay = 700) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, delay);
  });
};

/**
 * Applies the specified theme to the <html> element.
 *
 * This utility function dynamically adds or removes the Tailwind CSS `dark` class
 * on the <html> element, and sets the `color-scheme` style to ensure that the
 * appearance of the page matches the selected theme.
 *
 * @param theme - A string value of 'dark' or 'light' that represents the desired theme.
 *
 * The function can be extended to respect the user's system-level dark mode
 * preference using `window.matchMedia` to detect if the user prefers dark mode.
 *
 * Example:
 * const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
 * If the user prefers dark mode, it can automatically apply the dark theme.
 */
export const applyTheme = (theme) => {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }
};

/**
 * Detects the user's system-level theme preference.
 *
 * This function checks whether the user has set their operating system to prefer
 * a dark color scheme or a light color scheme. It uses the `window.matchMedia`
 * method to check for the `(prefers-color-scheme: dark)` media query.
 *
 * @returns A string value of 'dark' if the user prefers dark mode, or 'light' if they prefer light mode.
 */
export const detectSystemThemePreference = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

/**
 * Applies the specified color palette to the document.
 *
 * @param {*} rgbString
 * @returns
 */
export const applyPalette = (rgbString) => {
  if (!rgbString) return;

  const root = document.documentElement;
  root.style.setProperty('--accentColor', rgbString); // ex: "0, 72, 204"
};

/**
 * Scrolls the page smoothly to the top of the document.
 * This function uses the browser's built-in scroll behavior to smoothly
 * transition the scroll position of the document to the top.
 */
export const smoothScrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

/**
 * Determines if the navbar should be collapsed (small version).
 * The navbar is considered collapsed when it's not open and the device is in desktop mode.
 *
 * @param {boolean} open - Represents if the navbar is currently open.
 * @param {boolean} desktop - Indicates if the device is in desktop mode.
 * @returns {boolean} - Returns true if the navbar should be in its collapsed (small) state.
 */
export const isNavbarCollapsed = (open, desktop) => {
  if (!desktop) {
    return !desktop && !open;
  }

  return open && desktop;
};

/**
 * Combines the first name and last name into a full name string.
 * If both names are missing, returns a default name.
 *
 * @param {string} [firstName] - The first name of the user.
 * @param {string} [lastName] - The last name of the user.
 * @returns {string} - The full name formatted as "First Last", or "Default FullName" if neither name is provided.
 */
export const getUserFullName = (firstName = '', lastName = '') => {
  // A default parameter only covers `undefined`. An API that reports an unknown
  // name as an explicit `null` — as the activity feed does for an actor whose
  // account has since been deleted — went straight past it and threw on
  // `.trim()`, taking the whole page down with it.
  const formattedFirstName = String(firstName ?? '').trim();
  const formattedLastName = String(lastName ?? '').trim();

  if (!formattedFirstName && !formattedLastName) {
    return 'Default FullName'; // Default name when both are missing
  }

  return `${formattedFirstName} ${formattedLastName}`.trim();
};

/**
 * Generates an invoice title based on the provided document number and customer name.
 *
 * @param {string} [documentNumber=''] - The invoice document number.
 * @param {string} [customerName=''] - The name of the customer.
 * @param {string} [fallbackText='Default Invoice Title'] - The fallback text to use if both documentNumber and customerName are empty.
 * @returns {string} The formatted invoice title.
 */
export const getInvoiceTitle = (
  documentNumber = '',
  customerName = '',
  fallbackText = 'Default Invoice Title'
) => {
  const formattedDocumentNumber = (documentNumber ?? '').trim();
  const formattedCustomerName = (customerName ?? '').trim();

  if (!formattedDocumentNumber && !formattedCustomerName) {
    return fallbackText;
  }

  return `${formattedDocumentNumber} ${formattedCustomerName}`.trim();
};

/**
 * Returns the company name or a default value if not provided.
 *
 * @param {string} companyName - The name of the company.
 * @returns {string} - The company name or "Default Company" if none is provided.
 */
export const getDefaultCompanyName = (companyName) => {
  return companyName?.trim() || 'Default Company';
};

/**
 * Splits a pathname string into an array of path segments, excluding empty segments.
 * Filters out specific segments like 'view' and 'edit'.
 *
 * @param {string} pathname - The pathname string to split into path segments.
 * @returns {string[]} - An array of path segments extracted from the pathname.
 */
export const extractPathSegments = (pathname) => {
  const excludedSegments = [OPERATION.view, OPERATION.edit];
  const segments = pathname.split('/').filter((segment) => segment !== '');

  const filteredSegments = segments.filter(
    (segment) => !excludedSegments.includes(segment)
  );

  return filteredSegments;
};

/**
 * Generates a breadcrumb path from an array of pathname segments up to the specified index.
 * @param index The index up to which segments of the pathname array should be included in the breadcrumb.
 * @param pathname An array of pathname segments.
 * @returns The generated breadcrumb path string.
 */
export const generateBreadcrumbPath = (index, paths) => {
  return '/' + paths.slice(0, index + 1).join('/');
};

/**
 * Formats an array of strings into a readable format with spaces between camelCase words
 * and separates each item with a comma and a space.
 * @param data An array of strings to be formatted.
 * @returns A formatted string with words split and capitalized, separated by commas.
 */
export const formatCamelCaseToReadable = (data) => {
  if (!Array.isArray(data)) {
    return ''; // If the input is not an array, return an empty string.
  }

  return data
    .map(
      (item) =>
        item
          .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between camelCase words.
          .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize the first letter.
    )
    .join(', '); // Join items with a comma and space.
};

/**
 * getLanguageFromCookiesOrDefault - Retrieves the language setting from cookies,
 * defaulting to English ('en') if the language cookie is not set.
 *
 * This function uses a CookieService to get the language preference stored in cookies.
 * If the specified cookie (e.g., 'i18next') is not found, it defaults to English ('en').
 *
 * @returns {string} - The language code from cookies or 'en' as the default language code.
 */
export const getLanguageFromCookiesOrDefault = () => {
  return CookieService.get('i18next') || 'en';
};

/**
 * formatDateTimeWithLocale - Transforms a date into a locale-aware string format,
 * including dynamic updates for date and time based on language changes.
 *
 * @param {string|Date} date - The date to format, which can be a date string or a Date object.
 * @param {Object} [options] - Optional formatting options to customize the date and time format.
 * @param {boolean} [includeTime=false] - If true, includes time in the formatted string.
 * @returns {string} - The formatted date (and time, if specified) string based on the active locale.
 */
export const formatDateTimeWithLocale = (
  date,
  options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
  includeTime = false
) => {
  // Dynamically fetch current language
  const language = getLanguageFromCookiesOrDefault();
  const locale = LOCALE_MAP[language] || 'en-GB';

  const dateOptions = { ...options };

  if (includeTime) {
    // Add time-specific options
    dateOptions.hour = '2-digit';
    dateOptions.minute = '2-digit';
    // dateOptions.second = '2-digit'; // Removed: only show hours and minutes
    dateOptions.hour12 = false; // Use 24-hour format; set to true for 12-hour format
  }

  // 🚨 A missing date must not print as a date. `new Date(null)` is the epoch,
  // so every archived invoice with no recorded archive date rendered as
  // 01/01/1970 — a plausible-looking value that is simply false. `undefined`
  // and a malformed string are just as bad, arriving as "Invalid Date".
  // Measured before this: 42 of the 57 call sites passed the value straight
  // through with no guard of their own, so the check belongs here rather than
  // at each of them.
  if (date === null || date === undefined || date === '') return '—';

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleString(locale, dateOptions);
};

/**
 * findRoleBadge - Retrieves the styling and metadata for a role badge based on a given key.
 *
 * This function looks for a role matching the provided `roleKey` in the `rolesOptions` array.
 * If a match is found, it returns the corresponding badge configuration.
 * Otherwise, it returns a default badge configuration, using the `roleKey` as the label.
 *
 * @param {string | string[]} roleKey - A single role key or an array of role keys to find.
 * @param {Array} rolesOptions - A list of role options, where each object includes `key`, `label`, and `className`.
 * @returns {Object} - The role badge object containing:
 *                     - `className` (string): CSS class for badge styling.
 *                     - `label` (string): Display label for the badge.
 *                     - `key` (string): Unique key for the role.
 */
export const findRoleBadge = (roleKey, rolesOptions) => {
  const defaultBadge = {
    className: 'bg-zinc-200 border-zinc-300', // Default styling for unknown roles
    label: Array.isArray(roleKey) ? roleKey.join(', ') : roleKey, // Use roleKey or join multiple keys
    key: Array.isArray(roleKey) ? roleKey.join(',') : roleKey, // Keep the same format as label
  };

  if (!roleKey || !rolesOptions) {
    return defaultBadge;
  }

  if (Array.isArray(roleKey)) {
    return (
      rolesOptions.find(({ key }) => roleKey.includes(key)) || defaultBadge
    );
  }

  return rolesOptions.find(({ key }) => key === roleKey) || defaultBadge;
};

/**
 * convertToCamelCase - Converts a kebab-case string into camelCase format.
 *
 * This utility function transforms strings formatted as `kebab-case` into `camelCase`.
 * It replaces hyphens (`-`) with nothing and capitalizes the first letter of each subsequent word.
 * If the input is invalid (not a string), it safely returns an empty string.
 *
 * @param {string} input - The kebab-case string to convert.
 * @returns {string} - The converted camelCase string.
 */
export const convertToCamelCase = (input) => {
  if (typeof input !== 'string') {
    console.warn('Invalid input: Expected a string.');
    return '';
  }

  return input.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Compares two objects deeply and determines if they are identical.
 *
 * This function checks if both objects have the same keys and values,
 * including nested objects. It performs a recursive comparison for objects
 * inside objects and ensures equality for primitive values.
 *
 * @param {Object} obj1 - The first object to compare.
 * @param {Object} obj2 - The second object to compare.
 * @returns {boolean} - Returns true if objects are identical, otherwise false.
 */
export const compareObjectsDeeply = (obj1, obj2) => {
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    const areValuesObjects =
      typeof val1 === 'object' &&
      typeof val2 === 'object' &&
      val1 !== null &&
      val2 !== null;

    if (
      (areValuesObjects && !compareObjectsDeeply(val1, val2)) ||
      (!areValuesObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Removes specified keys from the given object.
 *
 * @param {Object} obj - The object from which keys will be removed.
 * @param {Array<string>} keysToRemove - An array of keys that should be deleted from the object.
 * @returns {Object} - A new object with the specified keys removed.
 *
 * Example:
 * const queryFilters = { offset: '1', limit: '10', email: undefined };
 * const updatedFilters = removeObjectKeys(queryFilters, ['email']);
 * console.info(updatedFilters); // { offset: '1', limit: '10' }
 */
export const removeObjectKeys = (obj, keysToRemove) => {
  // Create a shallow copy of the object to avoid mutating the original
  const updatedObject = { ...obj };

  // Iterate over the keys to remove and delete them from the object copy
  keysToRemove.forEach((key) => {
    delete updatedObject[key];
  });

  return updatedObject;
};

/**
 * Groups an array of objects by a specified key.
 *
 * This function takes an array of objects and a key extractor function
 * to group the objects by a specific property. It includes checks to
 * ensure valid inputs, preventing application crashes.
 *
 * @param {Array} array - The array of objects to be grouped.
 * @param {Function} keyExtractor - A function that extracts the grouping key from each object.
 * @returns {Object} An object where each key is a group, and the value is an array of objects in that group.
 *
 * @throws {TypeError} If the input is not an array or keyExtractor is not a function.
 */
export const groupArrayByKey = (array, keyExtractor) => {
  // Validate that the first argument is an array
  if (!Array.isArray(array)) {
    throw new TypeError('The first argument must be an array.');
  }

  // Validate that the second argument is a function
  if (typeof keyExtractor !== 'function') {
    throw new TypeError('The second argument must be a function.');
  }

  // Use reduce to group the array by the key extracted
  return array.reduce((acc, item) => {
    try {
      // Extract the key using the keyExtractor function
      const key = keyExtractor(item);

      // Ensure the key is not undefined or null
      if (key === undefined || key === null) {
        throw new Error('Key extractor function returned an invalid key.');
      }

      // Initialize the group if it doesn't exist
      if (!acc[key]) {
        acc[key] = [];
      }

      // Add the item to the group
      acc[key].push(item);
    } catch (error) {
      console.error('Error processing item:', item, error);
      // Optionally, decide whether to continue or rethrow the error
    }
    return acc;
  }, {});
};

/**
 * Converts an RGB array or string to a HEX color string.
 *
 * @param {string|array} rgb - The RGB color values as a string (e.g., "88, 13, 57")
 *                             or an array ([88, 13, 57]).
 * @returns {string|null} - The HEX color string (e.g., "#580d39") or null if the input is invalid.
 */
export function rgbToHex(rgb) {
  // Convert string input (e.g., "88, 13, 57") into an array of numbers
  const rgbValues = Array.isArray(rgb) ? rgb : rgb.match(/\d+/g)?.map(Number);

  // Validate the extracted values
  if (!rgbValues || rgbValues.length !== 3) {
    console.warn('Invalid RGB format:', rgb);
    return null;
  }

  // Convert each RGB value to a two-digit hexadecimal string and join them
  return `#${rgbValues
    .map((val) => val.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Converts an RGB color and optional opacity to a hexadecimal color string.
 *
 * @param {Object} params - The parameters object.
 * @param {number[]} params.color - Array of RGB values [r, g, b], each between 0 and 255.
 * @param {number} [params.opacity] - Optional opacity value between 0 and 1.
 * @returns {string} Hexadecimal color string (e.g., "#rrggbb" or "#rrggbbaa").
 * @throws {Error} If the color parameter is not an array with at least 3 numbers.
 */
export function colorToHex({ color, opacity }) {
  if (typeof color !== 'string') {
    throw new Error('Color must be a string in the format "r, g, b".');
  }

  const [r, g, b] = color.split(',').map((c) => parseInt(c.trim(), 10));

  const toHex = (n) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  if (opacity === undefined) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  const o = Math.min(1, Math.max(0, opacity));

  const rAdj = r + (255 - r) * (1 - o);
  const gAdj = g + (255 - g) * (1 - o);
  const bAdj = b + (255 - b) * (1 - o);

  return `#${toHex(rAdj)}${toHex(gAdj)}${toHex(bAdj)}`;
}

/**
 * Lightens a HEX color by a given percentage.
 *
 * @param {string} hex - The HEX color (e.g., "#580d39").
 * @param {number} percent - The percentage to lighten the color (0-100).
 * @returns {string} - The lightened HEX color.
 */
export function lightenColor(hex, percent) {
  if (!hex || !hex.startsWith('#')) return hex;

  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5, 7), 16);

  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/**
 * Retrieves the value of a CSS variable and converts it to HEX format.
 * Optionally lightens the color.
 *
 * @param {string} variableName - The name of the CSS variable (e.g., "--accentColor").
 * @param {number} [lightenPercent=0] - The percentage to lighten the color (0-100).
 * @returns {string|null} - The HEX value of the CSS variable or null if it cannot be converted.
 */
export function getCssVariableInHex(variableName, lightenPercent = 0) {
  const isDarkMode = document.documentElement.classList.contains('dark');
  const rootElement = isDarkMode
    ? document.querySelector('.dark:root')
    : document.documentElement;

  const rootStyles = getComputedStyle(rootElement);
  const cssVarValue = rootStyles.getPropertyValue(variableName).trim();

  let hexColor = rgbToHex(cssVarValue);

  return lightenPercent > 0 ? lightenColor(hexColor, lightenPercent) : hexColor;
}

/**
 * Checks if all values in an object are defined and not null or empty.
 * @param {Object} obj - The object to check.
 * @returns {boolean} Returns true if all values are defined, otherwise false.
 */
export const areAllValuesDefined = (obj) => {
  return Object.values(obj).every(
    (value) => value !== undefined && value !== null && value !== ''
  );
};

/**
 * Safely retrieves a nested value from an object using a dot-separated path.
 * If any part of the path is undefined, the function will return a default value.
 *
 * @param {Object} obj - The object to retrieve the value from.
 * @param {string} path - The dot-separated path to the desired value (e.g., "user.details.name").
 * @param {*} [defaultValue=undefined] - The value to return if the path does not exist.
 * @returns {*} - The value at the specified path or the default value.
 *
 * @example
 * const data = { user: { details: { name: "John" } } };
 * console.info(get(data, "user.details.name")); // Output: "John"
 * console.info(get(data, "user.age", 30)); // Output: 30
 */
export const get = (obj, path, defaultValue = undefined) => {
  if (!path) return defaultValue;

  return path.split('.').reduce((acc, key) => {
    return acc && acc[key] !== undefined ? acc[key] : defaultValue;
  }, obj);
};

/**
 * Transforms an array of objects by translating a specific key.
 *
 * @param {Array} options - The array of options to transform.
 * @param {(value: string) => string} t - Function to apply the translation.
 * @param {string} key - The key whose values should be translated.
 * @param {string} ns - The key whose options of useTranslation `namespace` should be translated.
 * @returns {Array} Transformed options array.
 */
export const translateOptions = (options, t, key = 'label', ns = 'forms') => {
  return options.map((item) => {
    return {
      ...item,
      [key]: t(item[key], { ns: ns }),
    };
  });
};

/**
 * This function constructs a URL for a given operation type (e.g., 'view', 'edit', 'users', 'employees')
 * getActionLinkUrl - Generates a URL for a specific operation and record.
 *
 * @param {string} route - The operation type (e.g., 'view', 'edit', 'users', 'employees').
 * @param {string} resource - The resource object to link to.
 * @param {string} queryParams - The query parameters string to append to the URL.
 * @returns {string} - The generated URL for the operation and record.
 */
export const getActionLinkUrl = ({ route, resource, queryParams }) => {
  if (!route) {
    throw new Error('Route type is required.');
  }

  return `${route}${resource ? `/${resource}` : ''}${queryParams ? `?${queryParams}` : ''}`;
};

/**
 * This function constructs a URL for a given operation type (e.g., 'view', 'edit', 'users', 'employees')
 * getActionLinkUrl - Generates a URL for a specific operation and record.
 *
 * @param {*} fn
 * @returns
 */
export const isFunction = (fn) => typeof fn === 'function';

/**
 * Capitalizes the first letter of a given string.
 *
 * @param {string} str - The string to capitalize.
 * @returns {string} The string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Groups an array of objects by the namespace extracted from the `type` property.
 *
 * Each object's `type` property is expected to be a string in the format "namespace.subKey".
 * The function organizes the data into an object where each key is a namespace,
 * and its value is an object mapping subKeys to their corresponding `values`.
 *
 * @param {Array<{type: string, values: any}>} data - The array of objects to group.
 * @returns {Object<string, Object<string, any>>} An object grouped by namespace and subKey.
 */
export const groupByNamespace = (data) => {
  const result = {};

  for (const item of data) {
    const [namespace, subKey] = item.type.split('.');
    if (!namespace || !subKey) continue;

    if (!result[namespace]) {
      result[namespace] = {};
    }

    result[namespace][subKey] = item.values;
  }

  return result;
};

/**
 * Recursively maps the values of an object or array to a format suitable for select components.
 * For arrays, each item is transformed into an object with `value` and `label` properties.
 * For objects, the function is applied recursively to each property.
 *
 * @param {Object|Array} obj - The object or array to be transformed.
 * @returns {Object|Array} The transformed object or array in select format.
 */
export const mapValuesToSelectFormat = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => ({
      ...item,
      value: item.key,
    }));
  }

  if (typeof obj === 'object' && obj !== null) {
    const result = {};
    for (const key in obj) {
      result[key] = mapValuesToSelectFormat(obj[key]);
    }
    return result;
  }

  return obj;
};

/**
 * Truncates a string to a specified maximum length and appends an ellipsis ("...") if necessary.
 *
 * @param {string} str - The string to be truncated.
 * @param {number} [maxLength=62] - The maximum allowed length of the string including the ellipsis.
 * @returns {string} The truncated string with an ellipsis if it exceeded the maximum length, otherwise the original string.
 */
export const truncateWithEllipsis = (str, maxLength = 62) => {
  if (str.length > maxLength) {
    return str.slice(0, maxLength - 3) + '...';
  }

  return str;
};
